import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/lib/hooks/useTheme';
import { useModulo } from '@/lib/hooks/useModulo';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '@/lib/api/admin';
import { useAuthStore } from '@/lib/stores/authStore';
import { LogoSVG } from '@/design-system/LogoSVG';
import { MODULOS } from '@/lib/modulos';

const MODULES = [
  {
    id: 'eleg',
    // Ver a nota em `lib/modulos.ts`: o rótulo mudou para não ficar "Eleg"
    // dentro de "Eleg360"; o `id` continua `eleg`, que é o que governa acesso.
    name: 'Inteligência Eleitoral',
    desc: 'Votações, território e histórico',
    /**
     * Palavra no lugar do símbolo, no topo do cartão. Vem de `MODULOS` para
     * que o cartão e o seletor da barra lateral não possam divergir — foi o
     * que aconteceu quando cada um tinha a sua cópia escrita à mão.
     */
    marca: MODULOS.eleg.marca,
    color: '#F58406',
    tag: 'ATIVO',
    home: '/eleg',
    segment: 0,
  },
  {
    id: 'gab',
    name: 'Gabinete360',
    desc: 'Contatos, demandas, agenda e ações',
    // Sem marca: este cartao usa o simbolo, como antes.
    marca: MODULOS.gab.marca,
    color: '#8B5CF6',
    tag: 'ATIVO',
    home: '/dashboard',
    segment: 4,
  },
] as const;

/**
 * Os quatro do ecossistema. No protótipo eram cartões mortos com "em breve";
 * aqui cada um leva à tela que ENTREGA aquilo hoje -- e a descrição diz o que
 * a pessoa vai encontrar, não o nome do produto futuro.
 *
 * `modulo` é o que a rota de destino exige: sem ele contratado, o cartão não
 * vira link, porque mandar alguém para uma tela que vai recusá-lo é pior do
 * que não oferecer o caminho.
 */
const ECOSSISTEMA = [
  {
    name: 'Atendimento',
    color: '#3DDC84',
    segment: 1,
    desc: 'Base de apoiadores, demandas e WhatsApp',
    destino: '/contatos',
    modulo: 'gab' as const,
    mod: 'gab' as const,
  },
  {
    name: 'Analytics TRE',
    color: '#3B82F6',
    segment: 2,
    desc: 'Votações apuradas e série histórica',
    destino: '/eleg',
    modulo: null,
    mod: 'eleg' as const,
  },
  {
    name: 'Território',
    color: '#22D3EE',
    segment: 5,
    desc: 'Dados públicos por localidade',
    destino: '/territorio',
    modulo: null,
    mod: 'eleg' as const,
  },
  {
    /**
     * O nome do protótipo era "Mídias e sentimento". A plataforma NÃO faz
     * escuta de mídias sociais -- não há ingestão, nem entidade, nem worker
     * que colete qualquer coisa de fora, e a própria tela de destino declara
     * essa lacuna. Anunciar "mídias e sentimento" no cartão mandaria a pessoa
     * procurar o que não existe; a descrição diz o que ela vai achar lá.
     */
    name: 'Comunicação',
    color: '#EC4899',
    segment: 6,
    desc: 'O que a população está pedindo',
    destino: '/falando',
    modulo: null,
    mod: 'eleg' as const,
  },
];

export function HubPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { setModulo } = useModulo();
  const token = useAuthStore((s) => s.token) || '';
  const logout = useAuthStore((s) => s.logout);

  // Os módulos disponíveis e o cargo são do mandato, não preferência local
  const {
    data: mandato,
    isLoading: carregandoMandato,
    isError: erroNoMandato,
    error: erroMandato,
  } = useQuery({
    queryKey: ['mandato'],
    queryFn: () => adminApi.getMandato(token),
    enabled: !!token,
  });

  const contratados = mandato?.modulos ?? [];
  const ativos = MODULES.filter((m) => contratados.includes(m.id as any));

  /**
   * O rodape precisa distinguir tres estados que antes eram um so. A versao
   * anterior escrevia "Carregando mandato..." sempre que `mandato` fosse
   * undefined -- inclusive quando a consulta ja tinha FALHADO. A tela afirmava
   * um carregamento que nao estava mais acontecendo, e quem lia ficava
   * esperando por algo que nunca ia chegar.
   */
  const rodapeDoMandato = mandato
    ? `${mandato.nome} · ${mandato.cidade} · ${mandato.uf}`
    : carregandoMandato
      ? 'Carregando mandato...'
      : erroNoMandato
        ? `Nao foi possivel carregar o mandato: ${
            (erroMandato as any)?.response?.data?.message ??
            (erroMandato as any)?.message ??
            'erro desconhecido'
          }`
        : 'Mandato nao carregado.';

  const handleSelect = (mod: 'gab' | 'eleg', home: string) => {
    setModulo(mod);
    navigate(home);
  };

  return (
    <div className="hub" role="main">
      {/* Botão de Alternância de Tema */}
      <button className="tgl" onClick={toggleTheme} aria-label="Alternar tema">
        {theme === 'dark' ? '☀️ Modo claro' : '🌙 Modo escuro'}
      </button>

      {/* Cabeçalho do Hub */}
      <div className="top" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <LogoSVG size={44} color="#F58406" segment="all" />
        <span className="wordmark" style={{ fontSize: 28 }}>Eleg360</span>
      </div>
      <div className="sub">Plataforma modular de inteligência política</div>

      {/* Cards de Módulos Ativos */}
      <div className="cards" role="list">
        {ativos.map((m) => (
          <div
            key={m.id}
            className="mc"
            style={{ '--mc': m.color } as React.CSSProperties}
            role="listitem"
            onClick={() => handleSelect(m.id as 'gab' | 'eleg', m.home)}
            tabIndex={0}
            aria-label={`Acessar módulo ${m.name}`}
          >
            <span className="tag">{m.tag}</span>
            <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {m.marca ? (
                /*
                  O cartão do módulo eleitoral mostra a palavra em vez do
                  símbolo. Dizia "Eleg", que com a plataforma chamando-se
                  Eleg360 virava a marca do produto inteiro anunciando um
                  módulo só. Agora diz "Analytics", que é o que a pessoa
                  encontra do outro lado: votações apuradas e série histórica.
                */
                <span
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 700,
                    // Fixo em 42px, a palavra estourava o cartao no celular:
                    // "Analytics" saia maior que o proprio nome do modulo
                    // logo abaixo. `clamp` acompanha a largura e para nos 42.
                    fontSize: 'clamp(26px, 8vw, 42px)',
                    letterSpacing: '-0.02em',
                    color: m.color,
                  }}
                >
                  {m.marca}
                </span>
              ) : (
                <LogoSVG size={60} color={m.color} segment={m.segment} />
              )}
            </div>
            <h3>{m.name}</h3>
            <p>{m.desc}</p>
          </div>
        ))}
      </div>

      {/*
        Mandato carregado e sem nenhum modulo contratado e um estado valido do
        banco (a coluna nasce com lista vazia) e invalido de produto: sem modulo
        nenhum nao ha o que abrir. Antes, a tela simplesmente nao desenhava card
        algum e mantinha "Carregando mandato..." no rodape, o que fazia parecer
        defeito de rede. Agora diz o que houve e para onde ir.
      */}
      {mandato && ativos.length === 0 && (
        <div className="note warn" data-testid="sem-modulo-contratado" style={{ maxWidth: 560 }}>
          <b>Nenhum modulo contratado neste mandato.</b>
          A lista de modulos do cadastro esta vazia, entao nao ha o que abrir. Um titular ou
          administrador ativa em Administracao &rsaquo; Mandato.
        </div>
      )}

      {/* Módulos do Ecossistema */}
      <div className="soon" role="list" aria-label="Módulos do ecossistema">
        {ECOSSISTEMA.map((e) => {
          const liberado = !e.modulo || contratados.includes(e.modulo as any);
          const conteudo = (
            <>
              <LogoSVG size={34} color={e.color} segment={e.segment} />
              <b>{e.name}</b>
              {e.desc}
              <br />
              <span style={{ fontSize: 9, letterSpacing: '.12em', color: liberado ? e.color : undefined }}>
                {liberado ? 'ATIVO · ABRIR' : 'NÃO CONTRATADO'}
              </span>
            </>
          );

          // Sem o módulo contratado o cartão não vira link: a rota recusaria a
          // entrada, e um caminho que termina em recusa é pior que nenhum.
          if (!liberado) {
            return (
              <div key={e.name} role="listitem">
                {conteudo}
              </div>
            );
          }

          return (
            <div
              key={e.name}
              role="listitem"
              tabIndex={0}
              aria-label={`Abrir ${e.name}`}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setModulo(e.mod);
                navigate(e.destino);
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  setModulo(e.mod);
                  navigate(e.destino);
                }
              }}
            >
              {conteudo}
            </div>
          );
        })}
      </div>

      {/* Rodapé */}
      <div className="foot">
        {rodapeDoMandato}
        &nbsp;·&nbsp;{' '}
        <a
          role="button"
          tabIndex={0}
          onClick={() => {
            logout();
            navigate('/login');
          }}
          // Sem `href`, esta âncora não entra no tabIndex nativo do browser
          // nem responde a Enter/Espaço por padrão — é a única saída da
          // tela, então precisa ser alcançável por teclado.
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              logout();
              navigate('/login');
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          sair
        </a>{' '}
        &nbsp;·&nbsp; cada módulo acende seu segmento do anel
      </div>
    </div>
  );
}
